/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/**
 * 路由配置
 */

import { type Routes } from '@angular/router';
import { LicenseKey } from 'app/common/constants/license-key';
import { AuthGuardCanActivate, AuthGuardCanActivateChild, AuthGuardCanLoad, PermissionGuardCanActivate } from 'app/common/guards';
import { Error404Component } from 'app/feature/share/error-404/error-404.component';
import { ErrorOtherComponent } from 'app/feature/share/error-other/error-other.component';

import { FeatureConfig } from './feature';
import { SWITCH } from './switch';

// 根路由
export const rootRoutes: Routes = [
    {
        path: 'login',
        loadChildren: () => import('app/feature/auth/auth.module').then((m) => m.AuthModule),
        canLoad: [AuthGuardCanLoad],
    },
    {
        path: 'poweronfail',
        loadChildren: () => import('app/feature/auth/poweron-fail/poweron-fail.module').then((m) => m.PoweronFailModule),
        canLoad: [AuthGuardCanLoad],
        data: { isSuperAdmin: true },
    },
    {
        path: 'virtualmachine',
        loadChildren: () => import('app/feature/system/smart-container/virtual-machine/virtual-machine-console/virtual-machine-console.module').
            then((m) => m.VirtualMachineConsoleModule),
        canLoad: [AuthGuardCanLoad],
    },
    {
        path: 'home',
        loadChildren: () => import('app/feature/home/home.module').then((m) => m.HomeModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'vstore',
        loadChildren: () => import('app/feature/home-vstore/vstore.module').then((m) => m.VstoreModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
    },
    {
        path: 'initialize',
        loadChildren: () => import('app/feature/initialize/initialize-configuration.module').then((m) => m.InitializeConfigurationModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        data: {
            isSuperAdmin: true,
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'service',
        loadChildren: () => import('app/feature/service/service.module').then((m) => m.ServiceModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
    {
        path: 'protection',
        loadChildren: () => import('app/feature/protection/protection.module').then((m) => m.ProtectionModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
    {
        path: 'monitor',
        loadChildren: () => import('app/feature/monitor/monitor.module').then((m) => m.MonitorModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
    {
        path: 'system',
        loadChildren: () => import('app/feature/system/system.module').then((m) => m.SystemModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
    {
        path: 'settings',
        loadChildren: () => import('app/feature/settings/settings.module').then((m) => m.SettingsModule),
        canLoad: [AuthGuardCanLoad],
        canActivate: [PermissionGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
    {
        path: 'simple',
        loadChildren: () => import('app/feature/simple-mode/simple-mode.module').then((m) => m.SimpleModeModule),
        canLoad: [AuthGuardCanLoad],
        data: { isSuperAdmin: true },
    },
    {
        path: 'error_other',
        component: ErrorOtherComponent,
        canLoad: [AuthGuardCanLoad],
    },
    {
        path: '',
        pathMatch: 'full',
        // 默认进入login，已登录用户会有缓存进入home
        redirectTo: 'login',
    },
    {
        path: '**',
        component: Error404Component,
        canLoad: [AuthGuardCanLoad],
        canActivate: [AuthGuardCanActivate],
        canActivateChild: [AuthGuardCanActivateChild],
    },
];

// 租户管理服务路由
export const vStoreServiceRoutes: Routes = [
    {
        path: 'v-store-service/v-store',
        loadChildren: () => import('app/feature/service/v-store-service/v-store/v-store.module').then((m) => m.VstoreModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportVStoreService,
        },
    },
];

// 租户自管理路由
export const vStoreHomeRoutes: Routes = [
    {
        path: 'home',
        loadChildren: () => import('app/feature/home-vstore/v-store-home/v-store-home.module').then((m) => m.VstoreHomeModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportVStoreService,
        },
    },
];

// 块服务路由
export const blockServiceRoutes: Routes = [
    {
        path: 'block/namespace-group',
        loadChildren: () => import('app/feature/service/block-service/lun-entry/lun-entry.module').then((m) => m.LunEntryModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !FeatureConfig.IsReplaceStrLunWithNamespace,
        },
    },
    {
        path: 'block/lun-group',
        loadChildren: () => import('app/feature/service/block-service/lun-entry/lun-entry.module').then((m) => m.LunEntryModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsReplaceStrLunWithNamespace,
        },
    },
    {
        path: 'block/vvol',
        loadChildren: () => import('app/feature/system/vvol/vvol.module').then((m) => m.VvolModule),
    },
    {
        path: 'block/channel-lun',
        loadChildren: () => import('app/feature/service/block-service/channel-lun/channel-lun.module').then((m) => m.ChannelLunModule),
    },
    {
        path: 'block/hosts',
        loadChildren: () => import('app/feature/service/block-service/hosts/hosts.module').then((m) => m.HostsModule),
    },
    {
        path: 'block/port-group',
        loadChildren: () => import('app/feature/service/block-service/port-group/port-group.module').then((m) => m.PortGroupModule),
    },
    {
        path: 'block/mapping-views',
        loadChildren: () => import('app/feature/service/block-service/mapping-views/mapping-views.module').then((m) => m.MappingViewsModule),
    },
    {
        path: 'container-storage/pod',
        loadChildren: () => import('app/feature/service/block-service/container-sevice/pod/pod.module')
            .then((m) => m.PodModule),
    },
    {
        path: 'container-storage/pv',
        loadChildren: () => import('app/feature/service/block-service/container-sevice/pv/pv.module')
            .then((m) => m.PvModule),
    },
];

// 文件服务路由
export const fileServiceRoutes: Routes = [
    {
        path: 'file/file-system',
        loadChildren: () => import('app/feature/service/file-service/file-system/file-system.module').then((m) => m.FileSystemModule),
    },
    {
        path: 'file/dtree',
        loadChildren: () => import('app/feature/service/file-service/dtree/dtree.module').then((m) => m.DtreeModule),
    },
    {
        path: 'file/share',
        loadChildren: () => import('app/feature/service/file-service/share/share.module').then((m) => m.ShareModule),
    },
    {
        path: 'file/data-turbo-client',
        loadChildren: () => import('app/feature/service/file-service/data-turbo-client/data-turbo-client.module')
            .then((m) => m.DataTurboClientModule),
        canActivate: [PermissionGuardCanActivate],
    },
    {
        path: 'file/nfs-client',
        loadChildren: () => import('app/feature/service/file-service/nfs-client/nfs-client.module')
            .then((m) => m.NfsClientModule),
        canActivate: [PermissionGuardCanActivate],
    },
    {
        path: 'file/access-user',
        loadChildren: () => import('app/feature/service/file-service/access-user/access-user.module').then((m) => m.AccessUserModule),
    },
    {
        path: 'file/quota',
        loadChildren: () => import('app/feature/service/file-service/quota/quota.module').then((m) => m.QuotaModule),
        canActivate: [PermissionGuardCanActivate],
        data: { depPermission: ['file_system_R'] },
    },
    {
        path: 'file/iniliziation',
        loadChildren: () => import('app/feature/service/file-service/iniliziation/iniliziation.module').then((m) => m.IniliziationModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            license: [[LicenseKey.NAS]],
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHideNasRoute,
        },
    },
];

// 网络路由
export const networkRoutes: Routes = [
    {
        path: 'network/fc',
        loadChildren: () => import('app/feature/service/network/fc-net/fc-net.module').then((m) => m.FcNetModule),
        canActivate: [PermissionGuardCanActivate],
    },
    {
        path: 'network/lif',
        loadChildren: () => import('app/feature/service/network/lif-net/lif-net.module').then((m) => m.LifNetModule),
    },
    {
        path: 'network/eth',
        loadChildren: () => import('app/feature/service/network/eth-net/eth-net.module').then((m) => m.EthNetModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'network/roce',
        loadChildren: () => import('app/feature/service/network/roce-net/roce-net.module').then((m) => m.RoceNetModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'network/net-space',
        loadChildren: () => import('app/feature/service/network/net-space/net-space.module').then((m) => m.NetSpaceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            // 与微存储无关，通过配置文件是否支持netSpace进行隔离
            isOceanMicroNotAvaliable: !SWITCH.SUPPORT_NET_SPACE,
        },
    },
    // 交换机端口
    {
        path: 'network/switch-port',
        loadChildren: () => import('app/feature/service/network/switch-net/switch-net.module').then((m) => m.SwitchNetModule),
        canActivate: [PermissionGuardCanActivate],
    },
];

// 资源调优路由
export const tuningRoutes: Routes = [
    {
        path: 'tuning/smart-cache',
        loadChildren: () => import('app/feature/service/resource-tuning/smart-cache/smart-cache.module').then((m) => m.SmartCacheModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportSmartCache,
        },
    },
    {
        path: 'tuning/smart-qos',
        loadChildren: () => import('app/feature/service/resource-tuning/smart-qos/qos-policy.module').then((m) => m.QosPolicyModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: SWITCH.IS_DIRECT_MODE,
        },
    },
    {
        path: 'tuning/smart-migration',
        // eslint-disable-next-line max-len
        loadChildren: () => import('app/feature/service/resource-tuning/smart-migration/smart-migration.module').then((m) => m.SmartMigrationModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportSmartMigration,
        },
    },
    {
        path: 'tuning/fs-smart-move',
        // eslint-disable-next-line max-len
        loadChildren: () => import('app/feature/service/resource-tuning/fs-smart-move/fs-smart-move-entry.module').then((m) => m.FsSmartMoveEntryModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !SWITCH.SUPPORT_SMART_MOVE,
        },
    },
    {
        path: 'tuning/smart-mobility',
        // eslint-disable-next-line max-len
        loadChildren: () => import('app/feature/service/resource-tuning/cloud-tier/cloud-tier-entry.module').then((m) => m.CloudTierEntryModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportSmartMobility,
        },
    },
];

// 容器存储
export const containerStorageRoutes: Routes = [
    {
        path: 'container-storage/pod',
        loadChildren: () => import('app/feature/service/block-service/container-sevice/pod/pod.module')
            .then((m) => m.PodModule),
    },
    {
        path: 'container-storage/pv',
        loadChildren: () => import('app/feature/service/block-service/container-sevice/pv/pv.module')
            .then((m) => m.PvModule),
    },
];

// 数据保护路由
export const protectionRoutes: Routes = [
    {
        path: 'snapshot',
        loadChildren: () => import('app/feature/protection/total-snapshot/total-snapshot.module')
            .then((m) => m.TotalSnapshotModule),
    },
    {
        path: 'clone',
        loadChildren: () => import('app/feature/protection/total-clone/total-clone.module')
            .then((m) => m.TotalCloneModule),
    },
    {
        path: 'replication',
        loadChildren: () => import('app/feature/protection/total-replication/total-replication.module')
            .then((m) => m.TotalReplicationModule),
    },
    {
        path: 'protection-group',
        loadChildren: () => import('app/feature/protection/x-protection-group/x-protection-group.module')
            .then((m) => m.XProtectionGroupModule),
    },
    {
        path: 'object/protect-group',
        loadChildren: () => import('app/feature/protection/object/protect-group-entry/protect-group-entry.module')
            .then((m) => m.ProtectGroupEntryModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'object/lun',
        loadChildren: () => import('app/feature/protection/object/lun/lun.module').then((m) => m.LunModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'object/filesystem',
        loadChildren: () => import('app/feature/protection/object/filesystem/filesystem.module').then((m) => m.FileSystemModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'v-store',
        loadChildren: () => import('app/feature/protection/vstore/vstore.module').then((m) => m.VstoreModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: !FeatureConfig.IsSupportVStoreService,
        },
    },
    {
        path: 'plan/hyper-cdp',
        loadChildren: () => import('app/feature/protection/schedule/hyper-cdp/hyper-cdp.module').then((m) => m.HyperCDPModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'plan/file-hyper-cdp',
        loadChildren: () => import('app/feature/protection/schedule/file-hyper-cdp/file-hyper-cdp.module')
            .then((m) => m.FileHyperCDPModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'config/remote-device',
        loadChildren: () => import('app/feature/protection/config/remote-device/remote-device.module').then((m) => m.RemoteDeviceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'config/hyper-metro-domain',
        loadChildren: () => import('app/feature/protection/config/hyper-metro-domain/hyper-metro-domain.module')
            .then((m) => m.HyperMetroDomainModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'config/deploy-antiransomware',
        // eslint-disable-next-line max-len
        loadChildren: () => import('app/feature/protection/config/deploy-antiransomware/deploy-antiransomware.module')
            .then((m) => m.DeployAntiransomwareModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
    {
        path: 'config/deploy-antiransomware',
        // eslint-disable-next-line max-len
        loadChildren: () => import('app/feature/protection/config/deploy-antiransomware/deploy-antiransomware.module')
            .then((m) => m.DeployAntiransomwareModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideProtection,
        },
    },
];

// 监控模块路由
export const monitorRoutes: Routes = [
    {
        path: 'alarms-and-events',
        loadChildren: () => import('app/feature/monitor/alarms-and-events/alarms-and-events.module').then((m) => m.AlarmsAndEventsModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'fault-tree-diagnosis',
        loadChildren: () => import('app/feature/monitor/fault-tree-diagnosis/fault-tree-diagnosis.module')
            .then((m) => m.FaultTreeDiagnosisModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            hide: !SWITCH.SUPPORT_FAULT_TREE_DIAGNOSIS,
        },
    },
    {
        path: 'performance',
        loadChildren: () => import('app/feature/monitor/performance-monitor/performance-monitor.module')
            .then((m) => m.PerformanceMonitorModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'performance/dashboard',
        loadChildren: () => import('app/feature/monitor/dashboard/dashboard.module').then((m) => m.DashboardModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHidePms,
        },
    },
    {
        path: 'performance/analysis',
        loadChildren: () => import('app/feature/monitor/analysis/analysis.module').then((m) => m.AnalysisModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'performance/performance-anomaly',
        loadChildren: () => import('app/feature/monitor/performance-anomaly/performance-anomaly.module')
            .then((m) => m.PerformanceAnomalyModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'performance/power-consumption',
        loadChildren: () => import('app/feature/monitor/power-consumption/power-consumption.module').then((m) => m.PowerConsumptionModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'task',
        loadChildren: () => import('app/feature/monitor/task/task.module').then((m) => m.TaskModule),
    },
    {
        path: 'schedule-report',
        loadChildren: () => import('app/feature/monitor/schedule-report/report-and-template/report-and-template.module')
            .then((m) => m.ReportAndTemplateModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHidePms,
        },
    },
    {
        path: 'forecasting',
        loadChildren: () => import('app/feature/monitor/forecasting/forecasting.module').then((m) => m.ForecastingModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHidePms,
        },
    },
];

// system模块路由
export const systemRoutes: Routes = [
    {
        path: 'hardware',
        loadChildren: () => import('app/feature/system/hardware/hardware.module').then((m) => m.HardwareModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'external-storage',
        loadChildren: () => import('app/feature/system/external-storage/external-storage.module').then((m) => m.ExternalStorageModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHideExternalStorage,
        },
    },
    {
        path: 'storage-pool',
        loadChildren: () => import('app/feature/system/storage-pool/storage-pool.module').then((m) => m.StoragePoolModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: SWITCH.IS_DIRECT_MODE,
        },
    },
    {
        path: 'smart-container',
        loadChildren: () => import('app/feature/system/smart-container/smart-container.module')
            .then((m) => m.SmartContainerModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            license: [[
                LicenseKey.SmartContainerLicense,
                LicenseKey.SmartContainerLicenseStandard,
                LicenseKey.SmartContainerLicensePremium,
                LicenseKey.CloudBackup,
                LicenseKey.DataBackUpCapacity,
                LicenseKey.AntiRansomware,
                LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                LicenseKey.S3,
                LicenseKey.VirtualEnvironmentEnabler,
            ]],
            isVstoreNotAvaliable: true,
        },
    },
];

// 设置模块路由
export const settingsRoutes: Routes = [
    {
        path: 'basic-infomation',
        loadChildren: () => import('app/feature/settings/basic-infomation/basic-infomation.module').then((m) => m.BasicInfomationModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'block-storage-service',
        loadChildren: () => import('app/feature/settings/block-storage-service/block-storage-service.module')
            .then((m) => m.BlockStorageServiceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'file-storage-service',
        loadChildren: () => import('app/feature/settings/file-storage-service/file-storage-service.module')
            .then((m) => m.FileStorageServiceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isOceanMicroNotAvaliable: FeatureConfig.IsHideNasRoute,
        },
    },
    {
        path: 'iniliziation',
        loadChildren: () => import('app/feature/settings/file-storage-service/iniliziation/iniliziation.module')
            .then((m) => m.IniliziationModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            license: [[LicenseKey.NAS]],
            isVstoreNotAvaliable: true,
            isOceanMicroNotAvaliable: FeatureConfig.IsHideNasRoute,
        },
    },
    {
        path: 'license-management',
        loadChildren: () => import('app/feature/settings/license-management/license-management.module')
            .then((m) => m.LicenseManagementModule),
    },
    {
        path: 'alarm/alarm-notify',
        loadChildren: () => import('app/feature/settings/alarm/alarm-notify/alarm-notify.module').then((m) => m.AlarmNotifyModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'alarm/alarm-event',
        loadChildren: () => import('app/feature/settings/alarm/alarm-event/alarm-event.module').then((m) => m.AlarmEventModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'alarm/alarm-dump',
        loadChildren: () => import('app/feature/settings/alarm/alarm-dump/alarm-dump.module').then((m) => m.AlarmDumpModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'alarm/alarm-mask',
        loadChildren: () => import('app/feature/settings/alarm/alarm-mask/alarm-mask.module').then((m) => m.AlarmMaskModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'alarm/alarm-level',
        loadChildren: () => import('app/feature/settings/alarm/alarm-level/alarm-level.module').then((m) => m.AlarmLevelModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'users-security/security-policy',
        loadChildren: () => import('app/feature/settings/users-security/security-policy/security-policy.module')
            .then((m) => m.SecurityPolicyModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'users-security/domain-auth',
        loadChildren: () => import('app/feature/settings/users-security/domain-auth/domain-auth.module').then((m) => m.DomainAuthModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'users-security/user-role',
        loadChildren: () => import('app/feature/settings/users-security/user-role/user-role.module').then((m) => m.UserRoleModule),
    },
    {
        path: 'users-security/user-config',
        loadChildren: () => import('app/feature/settings/users-security/user-config/user-config.module').then((m) => m.UserConfigModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'users-security/sso-server',
        loadChildren: () => import('app/feature/settings/users-security/sso-server/sso-server.module').then((m) => m.SsoServerModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'snmp/snmp-protocol',
        loadChildren: () => import('app/feature/settings/snmp/snmp-protocol/snmp-protocol.module').then((m) => m.SnmpProtocolModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'snmp/snmp-security-policy',
        loadChildren: () => import('app/feature/settings/snmp/snmp-security-policy/snmp-security-policy.module')
            .then((m) => m.SnmpSecurityPolicyModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'monitor',
        loadChildren: () => import('app/feature/settings/monitor/monitor.module').then((m) => m.MonitorModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'key-service',
        loadChildren: () => import('app/feature/settings/key-service/key-service.module').then((m) => m.KeyServiceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'certificate-management',
        loadChildren: () => import('app/feature/settings/certificate-management/certificate-management.module')
            .then((m) => m.CertificateManagementModule),
    },
    {
        path: 'e-service',
        loadChildren: () => import('app/feature/settings/e-service/e-service.module').then((m) => m.EServiceModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            isVstoreNotAvaliable: true,
        },
    },
    {
        path: 'container-configuration',
        loadChildren: () => import('app/feature/settings/container-configuration/container-configuration.module')
            .then((m) => m.ContainerConfigurationModule),
        canActivate: [PermissionGuardCanActivate],
        data: {
            license: [[
                LicenseKey.SmartContainerLicense,
                LicenseKey.SmartContainerLicenseStandard,
                LicenseKey.SmartContainerLicensePremium,
                LicenseKey.CloudBackup,
                LicenseKey.DataBackUpCapacity,
                LicenseKey.AntiRansomware,
                LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                LicenseKey.S3,
                LicenseKey.VirtualEnvironmentEnabler,
            ]],
            isVstoreNotAvaliable: true,
        },
    },
];


// 极简模式路由
export const simpleModeRoutes: Routes = [
    {
        path: 'home',
        loadChildren: () => import('app/feature/home-s/home-s.module').then((m) => m.HomeSModule),
    },
    {
        path: 'block/lun',
        loadChildren: () => import('app/feature/service/block-service/lun-entry/lun-entry.module').then((m) => m.LunEntryModule),
    },
    {
        path: 'block/hosts',
        loadChildren: () => import('app/feature/service/block-service/hosts/hosts.module').then((m) => m.HostsModule),
    },
    {
        path: 'file-service',
        loadChildren: () => import('app/feature/service/file-service/simple-file-service/simple-file-service.module')
            .then((m) => m.SimpleFileServiceModule),
    },
    {
        path: 'protection',
        loadChildren: () => import('app/feature/protection/simple-protection/simple-protection.module')
            .then((m) => m.SimpleProtectionModule),
    },
    {
        path: 'alarms-and-events',
        loadChildren: () => import('app/feature/monitor/alarms-and-events/alarms-and-events.module').then((m) => m.AlarmsAndEventsModule),
    },
    {
        path: 'performance-monitor',
        loadChildren: () => import('app/feature/monitor/performance-monitor-s/performance-monitor-s.module').then((m) => m.PerformanceMonitorSModule),
    },
    {
        path: 'monitor/schedule-report',
        loadChildren: () => import('app/feature/monitor/schedule-report/report-and-template/report-and-template.module')
            .then((m) => m.ReportAndTemplateModule),
    },
    {
        path: 'system/hardware',
        loadChildren: () => import('app/feature/system/hardware/hardware.module').then((m) => m.HardwareModule),
    },
    {
        path: 'settings',
        loadChildren: () => import('app/feature/settings/simple-settings/simple-settings.module').then((m) => m.SimpleSettingsModule),
    },
    {
        path: 'settings/dme-iq',
        loadChildren: () => import('app/feature/initialize/basic-configuration/dme-iq-configuration/dme-iq-aside/dme-iq-aside.module')
            .then((m) => m.DmeIqAsideModule),
    },
];
